import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Check, X } from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : 'Date à confirmer');

export const PreorderProductRow = ({ product, today, ordersCount, onReceive, onPostpone }) => {
  const [editDate, setEditDate] = useState(false);
  const [newDate, setNewDate] = useState(product.preorder_expected_date || '');
  const due = product.preorder_expected_date && product.preorder_expected_date <= today;
  const reserved = product.preorder_reserved || 0;

  return (
    <li className={`border p-4 flex flex-col md:flex-row md:items-center gap-4 ${due ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/10 bg-[color:var(--color-surface)]'}`}
      data-testid={`preorder-product-${product.id}`}>
      <div className="w-14 h-16 bg-black overflow-hidden flex-shrink-0">
        {product.image_url && <img src={product.image_url} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-500 text-[10px] uppercase tracking-widest">{product.brand}</p>
        <p className="text-white truncate">{product.name}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
          <span className={due ? 'text-amber-400' : 'text-zinc-400'} data-testid={`preorder-date-${product.id}`}>
            <CalendarClock className="w-3 h-3 inline mr-1" />
            {due ? 'Arrivée attendue : ' : 'Arrivée prévue : '}{fmtDate(product.preorder_expected_date)}
          </span>
          <span className="text-zinc-400">Commandé fournisseur : <span className="text-white">{product.preorder_qty || 0}</span></span>
          <span className="text-zinc-400">Réservé clients : <span className="text-[color:var(--color-primary)]">{reserved}</span> ({ordersCount} précommande{ordersCount > 1 ? 's' : ''})</span>
          {product.is_published === false && <span className="text-purple-400">Masqué du catalogue</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {editDate ? (
          <>
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              className="bg-transparent border border-white/20 text-white text-sm px-2 py-1.5 outline-none"
              data-testid={`preorder-postpone-input-${product.id}`} />
            <button type="button" onClick={() => { onPostpone(product, newDate); setEditDate(false); }}
              disabled={!newDate}
              className="p-1.5 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black disabled:opacity-40"
              data-testid={`preorder-postpone-confirm-${product.id}`}><Check className="w-4 h-4" /></button>
            <button type="button" onClick={() => setEditDate(false)}
              className="p-1.5 border border-white/10 text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <button type="button" onClick={() => setEditDate(true)}
            className="px-3 py-1.5 border border-white/10 text-zinc-300 hover:text-white text-sm"
            data-testid={`preorder-postpone-btn-${product.id}`}>Reporter</button>
        )}
        <Link to={`/admin/produits/${product.id}`}
          className="px-3 py-1.5 border border-white/10 text-zinc-300 hover:text-white text-sm">Modifier</Link>
        <button type="button" onClick={onReceive}
          className="px-3 py-1.5 bg-[color:var(--color-primary)] text-black hover:bg-[color:var(--color-primary-hover)] text-sm font-medium"
          data-testid={`preorder-receive-btn-${product.id}`}>Valider la réception</button>
      </div>
    </li>
  );
};

export const PreorderOrdersList = ({ orders }) => (
  <section>
    <h2 className="text-white text-lg mb-3">Précommandes clients en attente ({orders.length})</h2>
    {orders.length === 0 ? (
      <p className="text-zinc-500 text-sm border border-white/10 p-6 text-center" data-testid="preorder-orders-empty">
        Aucune précommande client pour le moment.
      </p>
    ) : (
      <ul className="space-y-2" data-testid="preorder-orders-list">
        {orders.map(o => (
          <li key={o.id} className="border border-white/10 bg-[color:var(--color-surface)] p-4" data-testid={`preorder-order-${o.id}`}>
            <div className="flex flex-wrap justify-between gap-2 text-sm">
              <span className="text-white">#{o.id.slice(-8).toUpperCase()} — {o.customer_name}</span>
              <span className="text-zinc-400">Livraison prévue : <span className="text-[color:var(--color-primary)]">{fmtDate(o.expected_date)}</span></span>
              <span className="text-[color:var(--color-primary)]">{Number(o.total || 0).toFixed(2)}€</span>
            </div>
            <p className="text-zinc-500 text-xs mt-1">
              {o.items.map(i => `${i.product_name} ×${i.quantity}`).join(' · ')}
            </p>
            {o.preorder_note && <p className="text-amber-400 text-xs mt-1">{o.preorder_note}</p>}
          </li>
        ))}
      </ul>
    )}
  </section>
);

export const ReceiveModal = ({ product, onClose, onConfirm }) => {
  const [qty, setQty] = useState(product.preorder_qty || 0);
  const reserved = product.preorder_reserved || 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="receive-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.40)] p-6 w-full max-w-md">
        <h3 className="text-white text-lg mb-1">Valider la réception</h3>
        <p className="text-zinc-400 text-sm mb-4">{product.brand} — {product.name}</p>
        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Quantité réellement reçue</label>
        <input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
          data-testid="receive-qty-input" autoFocus />
        <p className="text-zinc-500 text-xs mt-2">
          {reserved} pièce(s) réservée(s) par des clients. Le produit passe en ligne avec le stock reçu et les
          précommandes couvertes passent « En attente » pour préparation.
        </p>
        {Number(qty) < reserved && (
          <p className="text-amber-400 text-xs mt-2" data-testid="receive-short-warning">
            Attention : quantité inférieure aux réservations — certaines précommandes resteront en attente.
          </p>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 text-zinc-400 hover:text-white text-sm">Annuler</button>
          <button type="button" onClick={() => onConfirm(parseInt(qty || 0, 10))}
            className="px-4 py-2 bg-[color:var(--color-primary)] text-black hover:bg-[color:var(--color-primary-hover)] text-sm font-medium"
            data-testid="receive-confirm-btn">Mettre en ligne</button>
        </div>
      </div>
    </div>
  );
};
